import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { MessageCircle } from 'lucide-react';
import ChatOverlay from '../../pages/chat/screens/ChatOverlay';
import { useChatStore } from '../../store/chat_store';
import api from '../../services/api';

const pageNames = {
  '/': 'Dashboard',
  '/teams': 'Space Designs Teams',
  '/customers': 'Space Designs Customers',
  '/estimations': 'Estimations Panel',
  '/orders': 'Orders Tracker',
  '/inventory': 'Inventory Manager',
  '/expenses': 'Expense Claims',
  '/accounts': 'Client Accounts',
  '/documents': 'Document Vault',
  '/settings': 'Settings',
  '/home': 'Space Designs Home',
  '/notifications': 'Notifications',
};

export const Header = ({ sidebarCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { pageTitle } = useUIStore();
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { conversations } = useChatStore();
  
  const unreadChatCount = conversations.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0);

  const [myStores, setMyStores] = useState([]);

  React.useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await api.get('/notifications?limit=5');
        if (res.data?.success) {
          setNotifications(res.data.data);
          setUnreadCount(res.data.unreadCount);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };

    const fetchStores = async () => {
      try {
        const res = await api.get('/auth/my-stores');
        if (res.data?.success) {
          const storesData = res.data.data;
          setMyStores(storesData);
          
          const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
          if (currentUser.storeRef && currentUser.storeRef !== 'all') {
             const activeStore = storesData.find(s => s._id === currentUser.storeRef);
             if (activeStore && activeStore.storeName !== currentUser.storeName) {
                 currentUser.storeName = activeStore.storeName;
                 localStorage.setItem('company_user', JSON.stringify(currentUser));
             }
          }
        }
      } catch (err) {
        console.error("Failed to fetch stores:", err);
      }
    };

    fetchNotifs();
    fetchStores();
    
    // Refresh periodically (every 15 seconds)
    const interval = setInterval(fetchNotifs, 15000);

    // Listen for manual refetch triggers
    const handleRefetch = () => fetchNotifs();
    window.addEventListener('refetchNotifications', handleRefetch);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refetchNotifications', handleRefetch);
    };
  }, [location.pathname]);

  const handleSwitchStore = (storeId) => {
    const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
    if (currentUser._id) {
      currentUser.storeRef = storeId;
      if (storeId === 'all') {
         currentUser.storeName = 'All Stores';
      } else {
         const store = myStores.find(s => s._id === storeId);
         if (store) currentUser.storeName = store.storeName;
      }
      localStorage.setItem('company_user', JSON.stringify(currentUser));
      window.location.reload(); // Hard reload to reset all states and refetch with new store header
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  let pageName = Object.entries(pageNames).find(([path]) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  })?.[1] || 'Space Designs';

  const finalPageName = pageTitle || pageName;

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <header className={`fixed top-0 right-0 h-[64px] bg-white/90 backdrop-blur-xl border-b border-[#ECE7DE] flex items-center justify-between px-8 z-30 transition-all duration-300 ${sidebarCollapsed ? 'left-16' : 'left-[260px]'}`}>
      <div className="flex items-center gap-3">
        <h1 className="font-serif font-bold text-[18px] text-[#1A1A1A] tracking-tight">{finalPageName}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Chat / Messages */}
        <div className="relative">
          <div className="relative group">
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className="relative w-10 h-10 flex items-center justify-center rounded-xl text-[#626262] hover:bg-[#F7F3EC] hover:text-[#C91D25] transition-all duration-200"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={1.8} />
              {unreadChatCount > 0 && (
                <>
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#C91D25] border border-white text-white text-[9px] font-bold rounded-full flex items-center justify-center z-10 shadow-sm leading-none">
                    {unreadChatCount}
                  </span>
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-[#C91D25] rounded-full animate-ping opacity-25"></span>
                </>
              )}
            </button>
            {/* Tooltip */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 text-[11px] font-medium text-[#626262] whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
              Internal Chat
            </div>
          </div>

          {chatOpen && createPortal(
            <>
              <div className="fixed inset-0 bg-black/20 z-[9998]" onClick={() => setChatOpen(false)} />
              <ChatOverlay onClose={() => setChatOpen(false)} />
            </>,
            document.body
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative w-10 h-10 flex items-center justify-center rounded-xl text-[#626262] hover:bg-[#F7F3EC] hover:text-[#C91D25] transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <>
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#C91D25] border border-white text-white text-[9px] font-bold rounded-full flex items-center justify-center z-10 shadow-sm leading-none">
                  {unreadCount}
                </span>
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-[#C91D25] rounded-full animate-ping opacity-25"></span>
              </>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.08)] py-3 border border-[#ECE7DE] z-50">
              <div className="px-4 pb-2 mb-2">
                <div className="flex items-center justify-between">
                  <span className="font-serif font-bold text-[14px] text-[#1A1A1A]">Notifications</span>
                  <span className="text-[11px] font-medium text-[#A27327]">{unreadCount} new</span>
                </div>
              </div>
              <div className="flex flex-col max-h-[300px] overflow-y-auto custom-scrollbar">
                {notifications.length === 0 ? (
                  <div className="p-6 flex flex-col items-center justify-center text-center">
                    <span className="text-2xl mb-2">📭</span>
                    <div className="text-sm font-medium text-[#1A1A1A]">No new notifications</div>
                    <div className="text-xs text-[#626262] mt-1">You're all caught up!</div>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif._id}
                      className={`p-3 border-b border-[#F7F3EC] cursor-pointer hover:bg-[#F7F3EC]/60 transition-colors flex items-start gap-3 ${!notif.read ? 'bg-[#F7F3EC]/30' : ''}`}
                      onClick={() => {
                        if (!notif.read) handleMarkAsRead(notif._id);
                        if (notif.link) {
                          setNotifOpen(false);
                          navigate(notif.link);
                        }
                      }}
                    >
                      <div className="flex-1">
                        <p className={`text-xs ${!notif.read ? 'font-semibold text-[#1A1A1A]' : 'text-[#626262]'}`}>
                          {notif.message}
                        </p>
                        <span className="text-[10px] text-[#626262] mt-1 block">
                          {new Date(notif.createdAt).toLocaleDateString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-[#C91D25] rounded-full mt-1.5 flex-shrink-0"></div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="p-3 text-center border-t border-[#ECE7DE]">
                <Link to="/notifications" onClick={() => setNotifOpen(false)} className="text-xs text-[#C91D25] font-semibold hover:underline">View all notifications</Link>
              </div>
            </div>
          )}
        </div>

        {/* Store Switcher */}
        <div className="relative border-l border-[#ECE7DE] pl-5 ml-1 hidden sm:flex">
          <div className="group relative">
            <button className="flex items-center gap-2 bg-[#F7F3EC] hover:bg-[#ECE7DE] px-3 py-1.5 rounded-lg transition-colors cursor-pointer text-left">
              <div className="flex flex-col">
                <span className="text-[10px] text-[#A27327] font-bold uppercase tracking-widest leading-none">Active Store</span>
                <span className="text-[12px] font-semibold text-[#1A1A1A] leading-tight mt-0.5 truncate max-w-[120px]">
                  {(() => {
                    const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
                    if (currentUser.storeRef === 'all') return 'All Stores';
                    const activeStore = myStores.find(s => s._id === currentUser.storeRef);
                    return activeStore ? activeStore.storeName : 'Loading...';
                  })()}
                </span>
              </div>
              <svg className="w-4 h-4 text-[#626262]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-[0_16px_40px_rgba(0,0,0,0.1)] py-2 border border-[#ECE7DE] opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 z-50">
              <div className="px-3 pb-2 mb-2 border-b border-[#F7F3EC]">
                <span className="text-[11px] font-bold text-[#626262] uppercase tracking-wider">Switch Store</span>
              </div>
              
              <div className="flex flex-col max-h-[200px] overflow-y-auto custom-scrollbar">
                {myStores.length === 0 ? (
                   <div className="px-4 py-3 text-xs text-[#626262]">No other stores available.</div>
                ) : (
                  (() => {
                    const currentUser = JSON.parse(localStorage.getItem('company_user') || '{}');
                    const isGlobalRole = currentUser.role === 'admin' || currentUser.role === 'owner';
                    const currentStoreRef = currentUser.storeRef;
                    
                    const storeOptions = [...myStores];
                    if (isGlobalRole) {
                      storeOptions.unshift({ _id: 'all', storeName: 'All Stores' });
                    }

                    return storeOptions.map(store => {
                      const isActive = currentStoreRef === store._id;
                      return (
                        <button
                          key={store._id}
                          onClick={() => {
                            if (!isActive) handleSwitchStore(store._id);
                          }}
                          className={`px-4 py-2.5 text-left transition-colors flex flex-col gap-0.5 ${isActive ? 'bg-[#F7F3EC] cursor-default' : 'hover:bg-[#F7F3EC]/50 cursor-pointer'}`}
                        >
                          <span className={`text-[12px] font-semibold ${isActive ? 'text-[#C91D25]' : 'text-[#1A1A1A]'}`}>
                            {store.storeName}
                          </span>
                          {store.storeCode && (
                            <span className="text-[10px] text-[#A27327] uppercase tracking-wider">{store.storeCode}</span>
                          )}
                        </button>
                      );
                    });
                  })()
                )}
                <div className="border-t border-[#ECE7DE] mt-1 pt-1">
                  <button 
                    onClick={() => {
                      localStorage.removeItem('company_token');
                      localStorage.removeItem('company_user');
                      window.location.href = '/login';
                    }}
                    className="w-full px-4 py-2 text-left text-xs text-[#626262] font-semibold hover:bg-[#F7F3EC] hover:text-[#C91D25] transition-colors"
                  >
                    Logout / Re-authenticate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Profile */}
        <div className="flex items-center gap-3 pl-5 ml-1 border-l border-[#ECE7DE]">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-tr from-[#C91D25] to-[#A27327] text-white font-bold text-[14px] shadow-sm select-none cursor-pointer">
            {user?.name ? user.name.charAt(0).toUpperCase() : 'O'}
          </div>
          <div className="flex flex-col hidden sm:flex cursor-pointer">
            <span className="text-[13px] font-semibold text-[#1A1A1A] leading-tight tracking-tight">
              {user?.name || 'Owner Admin'}
            </span>
            <span className="text-[11px] text-[#A27327] font-medium capitalize mt-0.5">
              {user?.role ? user.role.replace(/_/g, ' ') : 'Owner'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};




